import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Landmark, CheckCircle2, AlertCircle, ExternalLink, Loader2 } from "lucide-react";

export default function PayoutsPanel({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/artist/connect/status"); setStatus(data); }
    catch { setStatus({ connected: false, charges_enabled: false }); }
  };
  useEffect(() => { load(); }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/artist/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/artist/connect/dashboard");
      window.open(data.url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="p-6 font-mono text-xs text-gray-500 uppercase">Loading payout status...</div>;

  const active = status.charges_enabled && status.payouts_enabled;

  if (compact) {
    if (active) {
      return (
        <Button data-testid="payouts-active-btn" variant="outline"
                onClick={openDashboard} disabled={busy}
                className="rounded-none border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500]/10 bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4">
          <CheckCircle2 className="h-4 w-4 mr-2" /> Payouts active
        </Button>
      );
    }
    return (
      <Button data-testid="add-payment-method-btn" onClick={startOnboarding} disabled={busy}
              className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10 px-4">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Landmark className="h-4 w-4 mr-2" />}
        Add payment method
      </Button>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="border border-[#2A2A2A] bg-[#121212] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="h-6 w-6 text-[#FF4500]" />
          <h3 className="font-display text-2xl">Payouts</h3>
        </div>
        <p className="font-mono text-xs text-gray-400 leading-relaxed mb-6">
          MeloCraft takes <span className="text-[#FF4500] font-bold">$0 platform fee</span> on song, album, tip, bundle & merch sales. You keep <span className="text-[#FF4500] font-bold">100% of every dollar</span> your fans spend (minus Stripe processing fees, which every payment platform charges). Connect your bank once — Stripe handles the rest, weekly auto-payouts.
        </p>

        {active ? (
          <div>
            <div className="flex items-center gap-2 border border-[#FF4500] bg-[#FF4500]/10 p-3 mb-4">
              <CheckCircle2 className="h-5 w-5 text-[#FF4500]" />
              <div>
                <div className="font-mono text-sm text-[#FF4500] font-bold uppercase tracking-widest">Bank connected · Payouts live</div>
                <div className="font-mono text-[10px] text-gray-400 mt-1">Money from your fans lands in your bank on Stripe's rolling schedule (weekly by default).</div>
              </div>
            </div>
            <Button data-testid="open-stripe-dash" onClick={openDashboard} disabled={busy} variant="outline"
                    className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-11 px-6">
              <ExternalLink className="h-4 w-4 mr-2" /> Open Stripe dashboard
            </Button>
          </div>
        ) : status.connected ? (
          <div>
            <div className="flex items-center gap-2 border border-yellow-500/40 bg-yellow-500/10 p-3 mb-4">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div>
                <div className="font-mono text-sm text-yellow-400 font-bold uppercase tracking-widest">Onboarding incomplete</div>
                <div className="font-mono text-[10px] text-gray-400 mt-1">Finish the Stripe form — bank details, ID, tax info — to enable payouts.</div>
              </div>
            </div>
            <Button data-testid="continue-onboarding-btn" onClick={startOnboarding} disabled={busy}
                    className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-6">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Continue onboarding
            </Button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Stat label="Platform fee" value="$0" accent />
              <Stat label="Artist keeps" value="100%" accent />
              <Stat label="Payout speed" value="Weekly" />
            </div>
            <Button data-testid="start-onboarding-btn" onClick={startOnboarding} disabled={busy}
                    className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-8">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Landmark className="h-4 w-4 mr-2" />}
              Connect bank via Stripe
            </Button>
            <p className="font-mono text-[10px] text-gray-500 mt-3">Takes ~5 minutes. Stripe verifies your identity + bank. Standard KYC.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className={`border p-3 ${accent ? "border-[#FF4500]/50 bg-[#FF4500]/5" : "border-[#2A2A2A] bg-black/30"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-display text-xl md:text-2xl mt-1 ${accent ? "text-[#FF4500]" : ""}`}>{value}</div>
    </div>
  );
}
